import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
//import { CameraIcon } from "@components/Icons/CameraIcon";
//import { PlusIcon } from "@components/Icons/PlusIcon";
import { CarouselItem, FanCarousel } from '@components/FanCarousel/FanCarousel';
import { SearchBar } from '@components/SearchBar';
import { FanItem } from '@components/TagPickerWheel';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { extraQueryParams } from '@utils/constants.ts';
import { sendSelectCompanionMessage } from '@utils/station';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';
import { getFanCompanions, userGenerationPromptKey } from '../../config.ts';
import { useCreateGeneration } from '../../hooks/apiHooks/generationsHooks';

export const Footer = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, signinRedirect } = useAuth();
  const isStationMode = useStationMode();
  const demoFans = useMemo(
    () => getFanCompanions(isStationMode),
    [isStationMode],
  );
  const initialFan = demoFans[0];
  const [selectedFanAlias, setSelectedFanAlias] = useState(
    initialFan?.alias_tag ?? '',
  );
  // Stores the raw English placeholder from DemoFan data. SearchBar maps it
  // to the current locale via `translateFanPlaceholder` at render time, so
  // language switches reflect immediately without resetting this state.
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(
    initialFan?.placeholder ?? '',
  );
  const [selectedFanIndex, setSelectedFanIndex] = useState<number | null>(
    initialFan ? 0 : null,
  );
  const [userPrompt, setUserPrompt] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const { activePopup, isMinimized, selectedContributorSlug } =
    useCommonState();
  const { activePlaylist } = useMusicState();
  const { mutateAsync: createGenerationMutation, isPending: isGenerating } =
    useCreateGeneration();
  const [, setUserGenerationPrompt] = useSessionStorage<string | null>(
    userGenerationPromptKey,
    null,
  );

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  // Detect mobile viewport (md breakpoint at 768px)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  /*const openAvatarPopup = useCallback(() => {
    setActivePopup('avatar');
  }, [setActivePopup]);*/

  // When contributor is selected from outside, set the fan alias
  useEffect(() => {
    if (selectedContributorSlug) {
      const fanIndex = demoFans.findIndex(
        (fan) => fan.slug === selectedContributorSlug,
      );
      if (fanIndex !== -1) {
        const fan = demoFans[fanIndex];
        setSelectedFanAlias(fan.alias_tag);
        setSelectedFanIndex(fanIndex);
        setSelectedPlaceholder(fan.placeholder);
      }
    }
  }, [selectedContributorSlug]);

  // create carousel items from demoFan data
  const carouselItems = useMemo((): CarouselItem[] => {
    return demoFans.map((fan) => ({
      video: fan.video_url,
      image: fan.image_url,
      artistName: fan.given_name,
      artistAliasTag: fan.alias_tag,
      title: fan.title,
      slug: fan.slug,
    }));
  }, []);

  // Convert demoFans to FanItem format for TagPickerWheel
  const allFans: FanItem[] = useMemo(() => {
    return demoFans.map((fan) => ({
      tag: fan.alias_tag,
      image: fan.image_url,
    }));
  }, []);

  const handleFanClick = useCallback(
    (index: number) => {
      const fan = demoFans[index];
      console.log('Clicked fan:', fan.alias_tag);

      setSelectedFanAlias(fan.alias_tag);
      setSelectedFanIndex(index);
      setUserPrompt('');
      setSelectedPlaceholder(fan.placeholder);

      // Send message to native device in Station mode
      if (isStationMode && fan.companion_id) {
        sendSelectCompanionMessage(fan.companion_id);
      }
    },
    [isStationMode],
  );

  const handleCenteredFanChange = useCallback(
    (index: number) => {
      const fan = demoFans[index];
      console.log('Centered fan changed:', fan.alias_tag, 'at index:', index);

      setSelectedFanIndex(index);

      if (selectedFanAlias) {
        setSelectedFanAlias(fan.alias_tag);
        setSelectedPlaceholder(fan.placeholder);
      }

      // send to station
      if (isStationMode && fan.companion_id) {
        sendSelectCompanionMessage(fan.companion_id);
      }
    },
    [selectedFanAlias, isStationMode],
  );

  const handleRemoveFan = useCallback(() => {
    setSelectedFanAlias('');
    setSelectedFanIndex(null);
    setSelectedPlaceholder('');
  }, []);

  // handle generation start when user submits
  const handleGenerationStart = useCallback(
    async (fullPrompt: string) => {
      if (selectedFanIndex === null || isGenerating) return;

      // Check if user is authenticated
      if (!isAuthenticated) {
        const fullPath = window.location.pathname + window.location.search;
        signinRedirect({
          extraQueryParams,
          state: { returnUrl: fullPath },
        }).catch((error) => {
          console.error('Error during sign-in redirect:', error);
        });
        return;
      }

      const fan = demoFans[selectedFanIndex];
      setUserGenerationPrompt(fullPrompt);

      if (fan.capability === 'music') {
        // Generate UUID immediately for instant navigation
        const uniqueId = uuidv4();

        navigate(`/generation/${fan.slug}/${uniqueId}?start=true`);

        try {
          const result = await createGenerationMutation({
            prompt: fullPrompt,
            musicStyle: fan.music_style,
            thumbnailUrl: fan.full_image_url,
            uniqueId,
            voiceId: fan.voice_id,
            language: fan.language ?? 'English',
            gender: fan.gender,
            artistTag:
              fan.artist_alias || fan.alias || fan.alias_tag || fan.given_name,
            stationMode: isStationMode,
            // numOutputs: Features.enableSingleSongMode ? 1 : undefined,
          });

          console.log('Generation started:', result);
        } catch (error) {
          console.error('Failed to start generation:', error);
        }
      } else if (fan.capability === 'designer') {
        navigate(
          `/${
            fan.slug
          }?start=true&designSession=true&userInput=${encodeURIComponent(
            fullPrompt,
          )}`,
        );
      } else {
        // this is hardcoded by value from ilya, need to change later to better logic
        navigate(
          `/${
            fan.slug
          }?start=true&openId=b892d2ae-2b74-412d-bd97-234304559bbb&userInput=${encodeURIComponent(
            fullPrompt,
          )}`,
        );
      }
    },
    [
      selectedFanIndex,
      isGenerating,
      isAuthenticated,
      createGenerationMutation,
      navigate,
      isMobile,
      isStationMode,
    ],
  );

  return (
    <div
      id="footer"
      className={`fixed ${
        isStationMode ? 'left-0 sm:left-0' : 'left-[280px] sm:left-[215px]'
      } z-10000 w-full ${
        isStationMode ? 'sm:w-full' : 'sm:w-[calc(100%-215px)]'
      } bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.9)_100%)] pb-0 sm:pb-8 px-0 font-[NapsterSans] pointer-events-none animate-footer ${
        activePopup === 'musicplayer' && activePlaylist && isMinimized
          ? 'bottom-[70px]'
          : 'bottom-0'
      } ${location.pathname === '/access-denied' ? 'hidden' : ''}`}>
      <div className=" sm:max-w-[480px] sm:m-auto pointer-events-auto">
        <div
          className={`flex w-full item-center justify-center px-4 pb-2 fan-carousel-container ${
            activePopup === 'audiobooks-popup' ||
            !isHomePage ||
            showSearchDropdown
              ? 'hidden'
              : ''
          }`}>
          <FanCarousel
            items={carouselItems}
            onItemClick={handleFanClick}
            onCenteredItemChange={handleCenteredFanChange}
            selectedContributorSlug={selectedContributorSlug}
          />
        </div>
        <div className="flex gap-[8px] px-4 relative footer-search-container">
          <SearchBar
            placeholder={
              selectedFanAlias ? t('footer.typeYourPrompt') : t('header.askOrSearch')
            }
            showSearchDropdown={showSearchDropdown}
            setShowSearchDropdown={setShowSearchDropdown}
            containerClassName="w-full"
            dropdownClassName="right-[60px]"
            externalValue={userPrompt}
            onExternalValueChange={setUserPrompt}
            selectedTag={selectedFanAlias}
            selectedPlaceholder={selectedPlaceholder}
            onRemoveTag={handleRemoveFan}
            onGenerationSubmit={handleGenerationStart}
            isGenerating={isGenerating}
            capability={
              selectedFanIndex !== null
                ? demoFans[selectedFanIndex]?.capability
                : undefined
            }
            selectedFanImage={
              selectedFanIndex !== null
                ? demoFans[selectedFanIndex]?.image_url
                : undefined
            }
            allFans={allFans}
            selectedFanIndex={selectedFanIndex}
            selectedContributorSlug={selectedContributorSlug}
          />
          {/* <div className="flex">
            <button onClick={openAvatarPopup}>
              <img
                className="flex w-[44px] h-[44px] min-w-[44px] max-w-full object-cover rounded-full"
                src="/assets/images/chat_avatar.png"
                alt="Companion"
              />
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
});

Footer.displayName = 'Footer';
